// src/pages/SafetyTips.js
import React from "react";
import { Link } from "react-router-dom";
import "./SafetyTips.css";

export default function SafetyTips() {
  const safetyCategories = [
    {
      title: "Before the Trip",
      icon: "📋",
      color: "#667eea",
      tips: [
        {
          title: "Research Your Travel Partner",
          description: "Check their profile thoroughly, read reviews and ratings from other travelers, and verify their social media presence if possible."
        },
        {
          title: "Share Your Itinerary",
          description: "Always share your complete travel plans with family or trusted friends, including accommodation details, flight information, and partner contact details."
        },
        {
          title: "Meet in Public First",
          description: "Before committing to a trip, arrange a video call or in-person meeting in a public place to ensure compatibility and safety."
        },
        {
          title: "Trust Your Instincts",
          description: "If something feels off or uncomfortable during initial conversations, don't proceed with the trip. Your safety comes first."
        },
        {
          title: "Verify Agency Credentials",
          description: "When booking through agencies, verify their business registration, read multiple reviews, and check their ratings on our platform."
        }
      ]
    },
    {
      title: "During the Trip",
      icon: "✈️",
      color: "#f5576c",
      tips: [
        {
          title: "Stay Connected",
          description: "Maintain regular communication with family/friends. Set up check-in times and stick to them."
        },
        {
          title: "Keep Valuables Secure",
          description: "Use hotel safes for important documents and valuables. Keep copies of important documents separately."
        },
        {
          title: "Be Aware of Your Surroundings",
          description: "Stay alert in unfamiliar areas, especially at night. Stick to well-lit, populated areas when possible."
        },
        {
          title: "Respect Boundaries",
          description: "Maintain personal boundaries with travel partners. It's okay to need alone time or to decline activities."
        },
        {
          title: "Emergency Contacts",
          description: "Keep local emergency numbers, embassy contacts, and your hotel information easily accessible at all times."
        },
        {
          title: "Document Everything",
          description: "Keep records of expenses, receipts, and any incidents. Take photos of important locations and contacts."
        }
      ]
    },
    {
      title: "Financial Safety",
      icon: "💰",
      color: "#f093fb",
      tips: [
        {
          title: "Split Expenses Fairly",
          description: "Use expense-splitting apps or keep detailed records. Discuss and agree on budget expectations before the trip."
        },
        {
          title: "Avoid Advance Payments",
          description: "Be cautious about making large advance payments to individuals. Use secure payment methods and get receipts."
        },
        {
          title: "Separate Your Money",
          description: "Don't keep all your money in one place. Use a mix of cash, cards, and digital payments."
        },
        {
          title: "Beware of Scams",
          description: "Be skeptical of deals that seem too good to be true. Verify all bookings independently."
        }
      ]
    },
    {
      title: "Health & Wellness",
      icon: "🏥",
      color: "#10b981",
      tips: [
        {
          title: "Travel Insurance",
          description: "Always purchase comprehensive travel insurance covering medical emergencies, trip cancellations, and lost belongings."
        },
        {
          title: "Know Your Limits",
          description: "Don't feel pressured to participate in activities you're uncomfortable with. Your comfort and safety matter."
        },
        {
          title: "Stay Hydrated & Rested",
          description: "Take care of your physical health. Fatigue can lead to poor decision-making."
        },
        {
          title: "Carry Medications",
          description: "Bring necessary medications in original packaging, along with prescriptions and a basic first-aid kit."
        }
      ]
    },
    {
      title: "Digital Safety",
      icon: "🔒",
      color: "#fbbf24",
      tips: [
        {
          title: "Protect Personal Information",
          description: "Don't share sensitive information like passport numbers, bank details, or home address unnecessarily."
        },
        {
          title: "Use Secure Networks",
          description: "Avoid accessing banking or sensitive accounts on public Wi-Fi. Use a VPN when possible."
        },
        {
          title: "Enable Location Sharing",
          description: "Use location-sharing features with trusted contacts, but be selective about who can track you."
        },
        {
          title: "Backup Important Data",
          description: "Keep digital and physical copies of important documents stored securely online and with someone at home."
        }
      ]
    },
    {
      title: "In Case of Emergency",
      icon: "🚨",
      color: "#ef4444",
      tips: [
        {
          title: "Know Local Emergency Numbers",
          description: "Research and save local emergency service numbers for police, ambulance, and fire services before arriving."
        },
        {
          title: "Contact Your Embassy",
          description: "In serious situations, contact your country's embassy or consulate for assistance and guidance."
        },
        {
          title: "Report to Platform",
          description: "If you experience issues with a travel partner or agency, report it to us immediately at gowithmebuddy1@gmail.com."
        },
        {
          title: "Document Incidents",
          description: "Keep detailed records, photos, and witness information for any incidents or problems encountered."
        },
        {
          title: "Seek Safe Shelter",
          description: "If you feel unsafe, go to a hotel, police station, or busy public place immediately."
        }
      ]
    }
  ];

  const redFlags = [
    "Requests for money before meeting",
    "Reluctance to provide verifiable information",
    "Pressure to make quick decisions",
    "Inconsistent or evasive communication",
    "Refusing video calls or public meetings",
    "No reviews or ratings on profile",
    "Offering deals significantly below market rates",
    "Requesting personal documents too early"
  ];

  return (
    <div className="safety-tips-container">
      {/* Header */}
      <div className="safety-header">
        <div className="safety-header-content">
          <h1 className="safety-title">🛡️ Safety Tips</h1>
          <p className="safety-subtitle">
            Your safety is our top priority. Follow these guidelines for a secure and enjoyable travel experience.
          </p>
        </div>
      </div>

      {/* Emergency Contact Banner */}
      <div className="emergency-banner">
        <div className="emergency-content">
          <div className="emergency-icon">🚨</div>
          <div className="emergency-text">
            <h3>Emergency?</h3>
            <p>If you're in immediate danger, contact local emergency services first, then reach out to us.</p>
          </div>
          <a href="mailto:gowithmebuddy1@gmail.com" className="emergency-btn">
            Report Issue
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="safety-content">
        <div className="safety-container">
          {/* Safety Categories */}
          {safetyCategories.map((category, index) => (
            <div key={index} className="safety-category">
              <div className="category-header" style={{ borderLeftColor: category.color }}>
                <span className="category-icon-large">{category.icon}</span>
                <h2 className="category-title">{category.title}</h2>
              </div>
              <div className="tips-grid">
                {category.tips.map((tip, tipIndex) => (
                  <div key={tipIndex} className="tip-card">
                    <h3 className="tip-title">
                      <span className="tip-number">{tipIndex + 1}</span>
                      {tip.title}
                    </h3>
                    <p className="tip-description">{tip.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Red Flags Section */}
          <div className="red-flags-section">
            <div className="red-flags-header">
              <h2 className="red-flags-title">
                <span className="red-flag-icon">🚩</span>
                Red Flags to Watch For
              </h2>
              <p className="red-flags-subtitle">
                Be cautious if you encounter any of these warning signs:
              </p>
            </div>
            <div className="red-flags-grid">
              {redFlags.map((flag, index) => (
                <div key={index} className="red-flag-item">
                  <span className="flag-icon">⚠️</span>
                  <span className="flag-text">{flag}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Resources */}
          <div className="resources-section">
            <h2 className="resources-title">Need More Help?</h2>
            <div className="resources-grid">
              <Link to="/help" className="resource-card">
                <span className="resource-icon">❓</span>
                <h3>Help Center</h3>
                <p>Find answers to common questions</p>
              </Link>
              <a href="mailto:gowithmebuddy1@gmail.com" className="resource-card">
                <span className="resource-icon">📧</span>
                <h3>Contact Support</h3>
                <p>Get direct assistance from our team</p>
              </a>
              <Link to="/terms" className="resource-card">
                <span className="resource-icon">📄</span>
                <h3>Terms & Policies</h3>
                <p>Review our guidelines and policies</p>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="safety-cta">
        <div className="cta-content">
          <h2>Ready to Start Your Safe Journey?</h2>
          <p>Connect with verified travelers and agencies on our platform</p>
          <div className="cta-buttons">
            <Link to="/find-partner" className="cta-btn primary">
              Find Travel Partners
            </Link>
            <Link to="/agencies" className="cta-btn secondary">
              Browse Agencies
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
